import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styles from './DataReviewHub.module.css';
import {PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {resolveQaRole} from '../../../utils/userRoles';

/* ─── Constants ──────────────────────────────────────────── */
const GAP_PX = 10;
const MIN_CARD_WIDTH = 120;
const TARGET_CARD_WIDTH = 200;
const CARD_HEIGHT_PX = 195;
const INITIAL_VISIBLE_ROWS = 6;
const ROWS_PER_SCROLL = 4;
const REVIEW_LOADING_CARD_COUNT = 15;

/* ─── Pure helpers ───────────────────────────────────────── */
function getTaskTitle(task) {
    if (task?.scenario) return task.scenario;
    if (task?.environment) return task.environment;
    if (task?.scene) return task.scene;
    if (task?.details_description) return task.details_description;
    if (task?.description) return task.description;
    return 'Scenario Unavailable';
}

function normalizeTaskKey(value) {
    const text = String(value ?? '').trim();
    if (!text) return '';
    const num = Number(text);
    return Number.isFinite(num) ? String(num) : text;
}

function computeLayout(wrapW, cardCount) {
    const cols = Math.max(1, Math.floor((wrapW + GAP_PX) / (TARGET_CARD_WIDTH + GAP_PX)));
    const w = Math.max(MIN_CARD_WIDTH, Math.floor((wrapW - (cols - 1) * GAP_PX) / cols));
    const showNav = cardCount > cols;
    return { cols, cardWidth: w, showNav };
}

// For row at index rowIndex with `cols` columns, returns the 0-based card index
// within the visible page that should autoplay video. Diagonal: col = rowIndex % cols.
function getVideoCardIndexForRow(rowIndex, cols, cardCount) {
    if (cardCount >= cols) {
        return rowIndex % cols;
    } else {
        return rowIndex % cols % cardCount;
    }
}

function ReviewHubSkeleton() {
    return (
        <div className={styles.hubPage} aria-hidden="true">
            <div className={styles.topBar}>
                <div className={styles.topBarSpacer} />
                <div className={`${styles.skeletonLine} ${styles.skeletonUploadCount}`} />
                <div className={`${styles.iconBtn} ${styles.skeletonIconBtn}`} />
            </div>
            <div className={styles.gallery}>
                <div className={styles.loadingReviewGrid}>
                    {Array.from({length: REVIEW_LOADING_CARD_COUNT}, (_, index) => (
                        <div key={index} className={`${styles.videoCard} ${styles.loadingVideoCard}`}>
                            <div className={styles.cardThumb}>
                                <div className={styles.thumbSkeleton} />
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.cardName}>
                                    <div className={`${styles.skeletonLine} ${styles.skeletonCardTitle}`} />
                                    <div className={`${styles.skeletonLine} ${styles.skeletonCardId}`} />
                                </div>
                                <div className={styles.skeletonCardButton} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ─── SVG assets ─────────────────────────────────────────── */
function RobotPlaceholder() {
    return (
        <svg className={styles.thumbPlaceholder} width="90" height="106"
             viewBox="0 0 100 120" fill="none" aria-hidden="true">
            <rect x="30" y="10" width="40" height="30" rx="4" fill="#888"/>
            <rect x="38" y="18" width="10" height="10" rx="2" fill="#bbb"/>
            <rect x="52" y="18" width="10" height="10" rx="2" fill="#bbb"/>
            <rect x="20" y="45" width="60" height="50" rx="4" fill="#666"/>
            <rect x="10" y="48" width="12" height="35" rx="3" fill="#777"/>
            <rect x="78" y="48" width="12" height="35" rx="3" fill="#777"/>
            <rect x="35" y="95" width="12" height="20" rx="3" fill="#666"/>
            <rect x="53" y="95" width="12" height="20" rx="3" fill="#666"/>
        </svg>
    );
}

function ChevronLeft() {
    return (
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
            <path d="M6 1.5L3 4.5l3 3" stroke="currentColor" strokeWidth="1.3"
                  strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
            <path d="M3 1.5l3 3-3 3" stroke="currentColor" strokeWidth="1.3"
                  strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
            <path d="M2 2l9 9M11 2l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
    );
}

/* ─── CategoryRow ────────────────────────────────────────── */
/**
 * Props:
 *   scenario        – string label for this group
 *   rowIndex        – 0-based index of this row among all visible rows (for diagonal video pattern)
 *   cards           – array of card objects for this scenario
 *   previewData     – Map<uploadId, { videoUrl, imageUrl, isLoading, isError }> from parent
 *   onNeedPreviews  – (uploadIds) fetch previews for these upload IDs
 *   onReview        – (taskId, uploadId) navigation handler
 */
function CategoryRow({ scenario, rowIndex, cards, previewData, onNeedPreviews, onReview }) {
    const trackRef = useRef(null);
    const wrapRef  = useRef(null);
    const offsetRef = useRef(0);
    const cardCountRef = useRef(cards.length);
    cardCountRef.current = cards.length;

    const [layout, setLayout] = useState({ cols: 5, cardWidth: TARGET_CARD_WIDTH, showNav: false });
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);

    const layoutRef   = useRef(layout);
    layoutRef.current = layout;
    const pageIndexRef   = useRef(pageIndex);
    pageIndexRef.current = pageIndex;
    const fetchedOnMountRef = useRef(false);

    const { userRole, qaRole } = useContext(SwapMainAppContext);
    const shouldShowUploadId = resolveQaRole(qaRole, userRole) === 'expert qa';

    const getPageUploadIds = useCallback((page, cols) => {
        const start = page * cols;
        const end   = Math.min(start + cols, cardCountRef.current);
        const ids = [];
        for (let i = start; i < end; i++) {
            const card = cards[i];
            if (card?.uploadId != null) ids.push(card.uploadId);
        }
        return ids;
    }, [cards]);

    /* Stable measure function — created once per mount */
    const measureRef = useRef(null);
    if (measureRef.current === null) {
        measureRef.current = () => {
            if (!wrapRef.current) return;
            const wrapW = wrapRef.current.clientWidth;
            if (wrapW === 0) return;
            const next = computeLayout(wrapW, cardCountRef.current);
            setLayout((prev) => {
                if (
                    prev.cols === next.cols &&
                    prev.cardWidth === next.cardWidth &&
                    prev.showNav === next.showNav
                ) return prev;

                if (!fetchedOnMountRef.current) {
                    fetchedOnMountRef.current = true;
                    const ids = [];
                    const end = Math.min(next.cols, cardCountRef.current);
                    for (let i = 0; i < end; i++) {
                        const card = cards[i];
                        if (card?.uploadId != null) ids.push(card.uploadId);
                    }
                    onNeedPreviews(ids);
                } else {
                    // Resize: fetch any newly revealed cards on the current page
                    const start = pageIndexRef.current * next.cols;
                    const end   = Math.min(start + next.cols, cardCountRef.current);
                    const ids = [];
                    for (let i = start; i < end; i++) {
                        const card = cards[i];
                        if (card?.uploadId != null) ids.push(card.uploadId);
                    }
                    if (ids.length > 0) onNeedPreviews(ids);
                }
                return next;
            });
        };
    }

    useEffect(() => {
        const measure = measureRef.current;
        measure();
        const ro = new ResizeObserver(measure);
        if (wrapRef.current) ro.observe(wrapRef.current);
        return () => ro.disconnect();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        measureRef.current();
    }, [cards.length]);


    // Load any card data that have a missing previewData (edge case to handle card re-ordering, a new filter from the parent component)
    useEffect(() => {
        const { cols } = layoutRef.current;
        if (cols === 0) return;
        const ids = getPageUploadIds(pageIndexRef.current, cols).filter(
            (id) => !previewData.has(id)
        );
        if (ids.length > 0) onNeedPreviews(ids);
    }, [cards, previewData, getPageUploadIds, onNeedPreviews]);

    /* Carousel offset helpers */
    const getMaxOffset = () => {
        if (!wrapRef.current) return 0;
        const { cardWidth } = layoutRef.current;
        const totalW = cardCountRef.current * (cardWidth + GAP_PX) - GAP_PX;
        return Math.max(0, totalW - wrapRef.current.clientWidth);
    };

    const applyOffset = (newOffset, animated = true) => {
        const max     = getMaxOffset();
        const clamped = Math.min(max, Math.max(0, newOffset));
        offsetRef.current = clamped;
        if (trackRef.current) {
            trackRef.current.style.transition = animated
                ? 'transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94)'
                : 'none';
            trackRef.current.style.transform = `translateX(-${clamped}px)`;
        }
        setPrevDisabled(clamped <= 0);
        setNextDisabled(clamped >= max);
    };

    useEffect(() => {
        applyOffset(offsetRef.current, false);
    }, [layout.cardWidth, cards.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePrev = () => {
        const { cols, cardWidth } = layoutRef.current;
        applyOffset(offsetRef.current - cols * (cardWidth + GAP_PX));
        const newPage = Math.max(0, pageIndexRef.current - 1);
        setPageIndex(newPage);
        setIsScrolling(true);
        setTimeout(() => setIsScrolling(false), 380);
        onNeedPreviews(getPageUploadIds(newPage, cols));
    };

    const handleNext = () => {
        const { cols, cardWidth } = layoutRef.current;
        applyOffset(offsetRef.current + cols * (cardWidth + GAP_PX));
        const maxPage = Math.ceil(cardCountRef.current / cols) - 1;
        const newPage = Math.min(maxPage, pageIndexRef.current + 1);
        setPageIndex(newPage);
        setIsScrolling(true);
        setTimeout(() => setIsScrolling(false), 380);
        onNeedPreviews(getPageUploadIds(newPage, cols));
    };

    const { cols, cardWidth, showNav } = layout;
    // The card index within the current page that should play video (diagonal pattern)
    const videoCardIndex = getVideoCardIndexForRow(rowIndex, cols, cardCountRef.current);

    return (
        <section className={styles.categorySection}>
            <div className={styles.categoryHeader}>
                <div className={styles.categoryLeft}>
                    <span className={styles.categoryTitle}>{scenario}</span>
                    <span className={styles.categoryCount}>
                        {cards.length} upload{cards.length !== 1 ? 's' : ''}
                    </span>
                </div>
                {showNav && (
                    <div className={styles.rowNav}>
                        <button
                            type="button"
                            className={styles.rowNavBtn}
                            disabled={prevDisabled}
                            onClick={handlePrev}
                            aria-label="Scroll left"
                        >
                            <ChevronLeft />
                        </button>
                        <button
                            type="button"
                            className={styles.rowNavBtn}
                            disabled={nextDisabled}
                            onClick={handleNext}
                            aria-label="Scroll right"
                        >
                            <ChevronRight />
                        </button>
                    </div>
                )}
            </div>

            <div
                className={`${styles.rowWrap}${isScrolling ? ` ${styles.rowWrapScrolling}` : ''}`}
                ref={wrapRef}
                style={{ height: CARD_HEIGHT_PX + 'px' }}
            >
                <div className={styles.rowTrack} ref={trackRef}>
                    {cards.map((card, cardIndex) => {

                        if (!card?.uploadId) return null;

                        const data = previewData.get(card.uploadId);

                        // The card at videoCardIndex on the current page plays video;
                        // all others show the thumbnail image.
                        const pageStart = pageIndex * cols;
                        const positionInPage = cardIndex - pageStart;
                        const isVideoCard = positionInPage === videoCardIndex;

                        const isLoading   = data?.isLoading ?? false;
                        const isError     = data?.isError   ?? false;
                        const imageUrl    = data?.imageUrl  ?? null;
                        const videoUrl    = data?.videoUrl  ?? null;

                        const showSkeleton = !data || isLoading;
                        const showVideo = !showSkeleton && isVideoCard && videoUrl && !isError;
                        // const showImage = !showSkeleton && !isError && imageUrl;
                        const showImage = !showSkeleton && !showVideo && !isError && imageUrl;
                        const showPlaceholder = !showSkeleton && !showVideo && !showImage;

                        return (
                            <div
                                key={card.uploadId}
                                className={styles.videoCard}
                                style={{ width: cardWidth + 'px' }}
                                onClick={() => onReview(card.taskId, card.uploadId)}
                            >
                                <div className={styles.cardThumb}>
                                    <div className={styles.thumbSkeleton} />
                                    {showVideo && (
                                        <video
                                            className={styles.cardVideo}
                                            src={videoUrl}
                                            muted
                                            autoPlay
                                            loop
                                            playsInline
                                            preload="auto"
                                            onCanPlay={(e) => e.currentTarget.classList.add(styles.mediaReady)}
                                        />
                                    )}
                                    {showImage && (
                                        <img
                                            className={styles.cardVideo}
                                            src={imageUrl}
                                            alt=""
                                            onLoad={(e) => e.currentTarget.classList.add(styles.mediaReady)}
                                        />
                                    )}
                                    {showPlaceholder && <RobotPlaceholder />}
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardName}>
                                        <span className={styles.cardNameText} title={card.title}>
                                            {card.title}
                                        </span>
                                        {shouldShowUploadId && (
                                            <span className={styles.cardUploadId}>#{card.uploadId}</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.cardBtn}
                                    >
                                        Review &amp; Earn
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ─── Main component ─────────────────────────────────────── */
const DataReviewHub = ({ dataTasks = [], qaUploads = [], isQaUploadsLoading = false }) => {
    const { gatewayToken } = useContext(SwapMainAppContext);
    const [searchText, setSearchText] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // How many rows are currently rendered (grows as user scrolls)
    const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);

    const searchInputRef = useRef(null);
    const sentinelRef    = useRef(null);
    const navigate       = useNavigate();

    // Map<uploadId, { videoUrl, imageUrl, isLoading, isError }>
    const [previewData, setPreviewData] = useState(new Map());
    // Prevents any uploadId from being fetched more than once
    const fetchedIdsRef = useRef(new Set());

    useEffect(() => {
        if (isSearchOpen) searchInputRef.current?.focus();
    }, [isSearchOpen]);

    const uploads = qaUploads || [];

    const taskCardGroups = useMemo(() => {
        const taskMap = new Map(
            (dataTasks || []).map((t) => [normalizeTaskKey(t?.task_id), t])
        );

        const cards = uploads
            .filter((u) => u && u.task_id != null && u.upload_id != null)
            .map((u) => {
                const task = taskMap.get(normalizeTaskKey(u.task_id)) || null;
                return {
                    uploadId: u.upload_id,
                    taskId:   u.task_id,
                    title:    String(u.task_category || '').trim() || getTaskTitle(task),
                };
            });

        const term     = searchText.trim().toLowerCase();
        const filtered = term
            ? cards.filter((c) => `${c.taskId} ${c.title} ${c.uploadId}`.toLowerCase().includes(term))
            : cards;

        const groups = new Map();
        filtered.forEach((card) => {
            if (!groups.has(card.title)) groups.set(card.title, []);
            groups.get(card.title).push(card);
        });

        // The API returns categories and cards in QA-priority order. Map keeps
        // insertion order, so grouping here does not override backend sorting.
        return Array.from(groups.entries());
    }, [dataTasks, uploads, searchText]);

    /* Reset visible row count when the list changes (search/sort) */
    useEffect(() => {
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
    }, [taskCardGroups]);

    /* IntersectionObserver sentinel — loads more rows when user scrolls near the bottom */
    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleRowCount((prev) =>
                        Math.min(prev + ROWS_PER_SCROLL, taskCardGroups.length)
                    );
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [taskCardGroups.length]);

    /**
     * Fetch preview data (video + image URLs) for upload IDs not yet requested.
     * De-duplicated via fetchedIdsRef so concurrent calls never double-fetch.
     */
    const handleFetchPreviews = useCallback(async (uploadIds) => {
        const toFetch = uploadIds.filter((id) => {
            if (id == null || fetchedIdsRef.current.has(id)) return false;
            fetchedIdsRef.current.add(id);
            return true;
        });
        if (toFetch.length === 0) return;

        setPreviewData((prev) => {
            const next = new Map(prev);
            toFetch.forEach((id) => next.set(id, { isLoading: true }));
            return next;
        });

        try {
            const res = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/qa/reviewable-uploads/previews`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${gatewayToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ uploads: toFetch }),
                }
            );

            if (!res.ok) throw new Error('non-ok response');
            const json = await res.json();
            if (!json.success || !Array.isArray(json.previews)) throw new Error('bad payload');

            setPreviewData((prev) => {
                const next = new Map(prev);
                // Mark any IDs we requested but received no entry for as error
                toFetch.forEach((id) => {
                    if (!next.has(id) || next.get(id)?.isLoading) {
                        next.set(id, { isError: true });
                    }
                });
                json.previews.forEach((item) => {
                    const id = item.upload_id;
                    if (id == null) return;
                    next.set(id, {
                        videoUrl: item.preview_video_url  || null,
                        imageUrl: item.preview_image_url  || null,
                        isLoading: false,
                        isError:   false,
                    });
                });
                return next;
            });
        } catch (_) {
            setPreviewData((prev) => {
                const next = new Map(prev);
                toFetch.forEach((id) => next.set(id, { isError: true }));
                return next;
            });
        }
    }, [gatewayToken]);

    const handleReview = useCallback((taskId, uploadId) => {
        navigate({ pathname: `/data/review/${taskId}`, search: `?upload=${uploadId}` });
    }, [navigate]);

    const handleToggleSearch = useCallback(() => {
        setIsSearchOpen((prev) => {
            if (prev) setSearchText('');
            return !prev;
        });
    }, []);

    if (isQaUploadsLoading) {
        return <ReviewHubSkeleton />;
    }

    if (!uploads.length) {
        return (
            <div className={styles.hubPage}>
                <div className={styles.emptyGallery}>No QA uploads ready for review.</div>
            </div>
        );
    }

    const validUploadCount  = taskCardGroups.reduce((s, [, c]) => s + c.length, 0);
    const visibleGroups     = taskCardGroups.slice(0, visibleRowCount);
    const hasMoreRows       = visibleRowCount < taskCardGroups.length;

    return (
        <div className={styles.hubPage}>
            <div className={styles.topBar}>
                <div className={styles.topBarSpacer} />

                <span className={styles.uploadCount}>
                    {validUploadCount} upload{validUploadCount !== 1 ? 's' : ''}
                </span>

                {isSearchOpen && (
                    <input
                        ref={searchInputRef}
                        type="text"
                        className={styles.topBarSearch}
                        placeholder="Search by task, title, or upload…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                )}

                <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={handleToggleSearch}
                    aria-label={isSearchOpen ? 'Close search' : 'Search uploads'}
                >
                    {isSearchOpen ? <CloseIcon /> : <SearchIcon />}
                </button>
            </div>

            <div className={styles.gallery}>
                {visibleGroups.length === 0 ? (
                    <div className={styles.emptyGallery}>No tasks match your search.</div>
                ) : (
                    visibleGroups.map(([scenario, cards], rowIndex) => (
                        <CategoryRow
                            key={scenario}
                            rowIndex={rowIndex}
                            scenario={scenario}
                            cards={cards}
                            previewData={previewData}
                            onNeedPreviews={handleFetchPreviews}
                            onReview={handleReview}
                        />
                    ))
                )}

                {/* Sentinel triggers loading the next batch of rows */}
                {hasMoreRows && <div ref={sentinelRef} className={styles.scrollSentinel} />}
            </div>
        </div>
    );
};

export default DataReviewHub;
